import type { DeskFinishedRun, DeskResponse, DeskWriting } from './api'
import type { LinkAction } from './links'

export type StepState = 'done' | 'now' | 'todo'
export type WritingStep = { label: string; state: StepState }

const STEPS = ['Gathering sources', 'Researching', 'Writing', 'Checking every claim', 'Choosing a photo and social posts']
// Index of the step each server stage is "on". Researching (1) is never a
// stage of its own: it reads as done once writing starts.
const STAGE_STEP: Record<string, number> = {
  starting: 0, sources: 0, writing: 2, revising: 2, 'fact-check': 3, photo: 4, saving: 4, social: 4,
}

export function writingSteps(stage: string | null | undefined): WritingStep[] {
  const at = STAGE_STEP[stage ?? ''] ?? 0
  return STEPS.map((label, i) => ({ label, state: i < at ? 'done' : i === at ? 'now' : 'todo' }))
}

export function dayPart(hourEt: number): 'morning' | 'afternoon' | 'evening' {
  if (hourEt < 12) return 'morning'
  if (hourEt < 18) return 'afternoon'
  return 'evening'
}

export function etHour(d: Date): number {
  const h = Number(new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: 'numeric', hourCycle: 'h23' }).format(d))
  return h % 24
}

export function needsYouSentence(n: number): string {
  if (n <= 0) return 'Nothing needs you right now.'
  if (n === 1) return 'One piece needs you.'
  return `${n} pieces need you.`
}

export function greeting(n: number, now: Date = new Date()): string {
  return `Good ${dayPart(etHour(now))}. ${needsYouSentence(n)}`
}

export type NeedsAction = LinkAction
export const ACTION_LABEL: Record<NeedsAction, string> = {
  review: 'Review', check_sources: 'Check sources', try_again: 'Try again', publish_public_copy: 'Publish public copy',
}

/** The subject a link's ?topic= carries, as the Classic page's own-subject box takes it. */
export const topicFromQuery = (search: URLSearchParams, max = 200): string => (search.get('topic') ?? '').trim().slice(0, max)

/** The greeting's count: the server's total, not the few rows the desk lists. */
export const needsCount = (d: { needs_count?: number; needs_you: unknown[] }): number => d.needs_count ?? d.needs_you.length

export const IDEAS_UNAVAILABLE = 'Ideas are unavailable right now.'

export function slotWhen(atIso: string, now: Date = new Date()): boolean {
  return new Date(atIso).getTime() > now.getTime()
}

/** Minutes since an ISO start, floored, never negative. */
export function minutesIn(startedIso: string | null | undefined, now: Date = new Date()): number | null {
  if (!startedIso) return null
  return Math.max(0, Math.floor((now.getTime() - new Date(startedIso).getTime()) / 60000))
}

export const FORMAT_LABEL: Record<string, string> = { short: 'Short', long: 'Long', deep: 'Deep research', legacy: 'Short' }
export const formatLabel = (f: string) => FORMAT_LABEL[f] ?? f

// ---- writing runs ----------------------------------------------------------------------------

/** Check every 5 s while something is writing, so the card follows it closely; once a minute otherwise. */
export const WRITING_POLL_MS = 5_000
export const IDLE_POLL_MS = 60_000
export const deskPollMs = (d: Pick<DeskResponse, 'writing'> | undefined) => (d?.writing ? WRITING_POLL_MS : IDLE_POLL_MS)

/** The Desk as it will be once the run just started is on it: shown straight away, before the next check. */
export function withRunStarted(d: DeskResponse | undefined, status: DeskWriting | undefined): DeskResponse | undefined {
  if (!d || !status?.running) return d
  return { ...d, writing: status, counts: { ...d.counts, writing: 1 } }
}

/** A piece landed (the run ended, or a version was saved mid-run): the piece lists must reload. */
type RunMark = { run_id?: number; result_draft_ids?: number[] }
export function piecesLanded(before: RunMark | null | undefined, after: RunMark | null | undefined): boolean {
  if (!before) return false
  if (!after || after.run_id !== before.run_id) return true
  return (after.result_draft_ids?.length ?? 0) > (before.result_draft_ids?.length ?? 0)
}

export function finishedRunHeading(r: Pick<DeskFinishedRun, 'status' | 'pieces'>): string {
  if (r.status === 'stopped') return 'Stopped'
  if (r.status === 'failed') return 'Did not finish'
  return r.pieces.length === 1 ? 'Ready to review' : `${r.pieces.length} ready to review`
}
